/**
 * 🎬 **AR 위에 얹는 영상** — 어떤 파일을 받고, 그게 어느 기기에서 나오나.
 *
 * ## 왜 예전엔 WebM 만 받았나
 * 이 자리는 원래 **「투명 영상」** 이었다. 인식한 그림 위에 캐릭터만 떠야 하니 배경이
 * 지워져야 하고, 배경을 지울 수 있는 건 **WebM(VP8/VP9 알파)** 뿐이었다.
 * 그런데 **사파리(아이폰)는 WebM 을 못 튼다** — 그래서 아이폰은 늘 연속 그림으로 떨어졌다.
 *
 * ## 그래서 MP4 도 받는다
 * MP4(H.264)는 **아이폰을 포함해 거의 모든 기기에서** 나온다. 대신 **배경이 안 지워진다** —
 * 네모난 영상이 그림 위에 얹힌다. 어느 쪽이 나은지는 **콘텐츠가 정한다**:
 * 캐릭터만 떠야 하면 WebM, 화면 가득 차는 장면이면 MP4 가 낫다.
 *
 * 그래서 코드가 고르지 않고 **운영자가 고르게** 하되, 무엇이 달라지는지 화면에 적는다.
 *
 * ## 🔴 「틀 수 있나」를 파일로 판단해야 한다
 * 예전 웹은 `canPlayWebm()` 으로 막았다. **파일이 MP4 여도** 웹엠을 못 트는 기기면
 * 건너뛰었다 — 올려도 아이폰에서 안 나온다. 판정은 **그 파일의 종류**로 해야 한다.
 */

/** 이 자리에서 받는 영상 형식. */
export const AR_VIDEO_FORMATS = ["webm", "mp4"] as const;
export type ArVideoFormat = (typeof AR_VIDEO_FORMATS)[number];

/** 주소 끝의 확장자로 종류를 본다. 물음표·해시가 붙어 있어도 떼고 본다. */
export function arVideoFormat(url: string | undefined | null): ArVideoFormat | undefined {
  if (!url) return undefined;
  const clean = url.split(/[?#]/)[0]!.toLowerCase();
  if (clean.endsWith(".webm")) return "webm";
  if (clean.endsWith(".mp4") || clean.endsWith(".m4v")) return "mp4";
  return undefined;
}

/**
 * 브라우저에게 물어볼 MIME 문자열. 코덱까지 적어야 사파리가 제대로 답한다
 * (`video/mp4` 만 주면 `""`(모름)을 주는 기기가 있다).
 */
export function arVideoMimes(fmt: ArVideoFormat | undefined): string[] {
  if (fmt === "webm") return ['video/webm; codecs="vp9"', 'video/webm; codecs="vp8"', "video/webm"];
  if (fmt === "mp4") return ['video/mp4; codecs="avc1.42E01E"', "video/mp4"];
  return [];
}

/**
 * **배경이 지워지나.** 화면에 적어 줄 말이 달라진다 —
 * 「캐릭터만 떠요」 ↔ 「네모난 영상으로 나와요」.
 */
export function arVideoHasAlpha(fmt: ArVideoFormat | undefined): boolean {
  return fmt === "webm";
}

/** 운영자에게 보여줄 한 줄. 올린 파일이 실제로 어떻게 보일지 그대로 적는다. */
export function arVideoNote(fmt: ArVideoFormat | undefined): string {
  if (fmt === "webm") {
    return "투명 배경이라 캐릭터만 떠요. 다만 아이폰에서는 안 나와서 ①연속 그림이 대신 나와요.";
  }
  if (fmt === "mp4") {
    return "아이폰을 포함해 거의 모든 기기에서 나와요. 다만 배경이 안 지워져서 네모난 영상으로 보여요.";
  }
  return "";
}
